import React, { useState, useEffect, useMemo } from 'react';
import './Module.css';
import Network from '../../../models/Network.js';
import { GET_DOCUMENT_RETRIEVAL_FLOW, GET_ONE_REPORT_FINDINGS } from '../../../urls.js';
import GraphTreeProtocol from '../graphics/GraphTreeProtocol.js';

const DemoClinicalFeedbackModule = ({ reportId, isSmallScreen, selectedReportName }) => {
    const [flowData, setFlowData] = useState(null);
    const [analysisData, setAnalysisData] = useState(null);
    const [loadingFlow, setLoadingFlow] = useState(true);
    const [loadingFindings, setLoadingFindings] = useState(true);
    const [error, setError] = useState(null);
    const [flowPollingAttempts, setFlowPollingAttempts] = useState(0);
    const [findingsPollingAttempts, setFindingsPollingAttempts] = useState(0);
    const [maxAttempts] = useState(20); // 20 attempts * 5 seconds = 100 seconds max

    // Mock user object for demo purposes
    const mockUser = { uid: 'demo-user' };

    // Check if this is a refusal report
    const isRefusalReport = selectedReportName === '2025-demo-01';

    // Memoized flow component to prevent unnecessary re-renders
    const FlowComponent = useMemo(() => {
        if (!flowData) return null;
        return <GraphTreeProtocol user={mockUser} reportId={reportId} />;
    }, [flowData, reportId]);

    // Poll for document retrieval flow
    useEffect(() => {
        let timeoutId;
        let isMounted = true;
        
        const pollForFlow = async () => {
            if (flowPollingAttempts >= maxAttempts || !loadingFlow || !isMounted) {
                setLoadingFlow(false);
                return;
            }

            try {
                setError(null);
                
                const flowUrl = GET_DOCUMENT_RETRIEVAL_FLOW;
                const flowDict = {
                    uid: mockUser.uid,
                    report_id: reportId,
                    CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
                };

                await Network.post(flowUrl, flowDict, (response) => {
                    if (!isMounted) return; // Check if component is still mounted
                    
                    if (Network.checkValidStatusAndResponse(response) && response.data.flowData) {
                        setFlowData(response.data.flowData);
                        setLoadingFlow(false);
                    } else {
                        // Continue polling only if still loading and mounted
                        if (loadingFlow && isMounted) {
                            setFlowPollingAttempts(prev => prev + 1);
                            timeoutId = setTimeout(pollForFlow, 5000);
                        }
                    }
                });

            } catch (err) {
                console.error('Error polling for flow data:', err);
                if (loadingFlow && isMounted) {
                    setFlowPollingAttempts(prev => prev + 1);
                    timeoutId = setTimeout(pollForFlow, 5000);
                }
            }
        };

        if (reportId && loadingFlow && !isRefusalReport) {
            pollForFlow();
        }

        // Cleanup timeout on unmount or when loading stops
        return () => {
            isMounted = false;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [reportId, loadingFlow, isRefusalReport]); // Removed flowPollingAttempts from dependencies to prevent spam

    // Poll for findings data
    useEffect(() => {
        let timeoutId;
        let isMounted = true;
        
        const pollForFindings = async () => {
            if (findingsPollingAttempts >= maxAttempts || !loadingFindings || !isMounted) {
                setLoadingFindings(false);
                return;
            }

            try {
                setError(null);
                
                const analysisUrl = GET_ONE_REPORT_FINDINGS;
                const analysisDict = {
                    uid: mockUser.uid,
                    report_id: reportId,
                    CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
                };

                await Network.post(analysisUrl, analysisDict, (response) => {
                    if (!isMounted) return; // Check if component is still mounted
                    
                    if (Network.checkValidStatusAndResponse(response) && response.data.analysisResults && response.data.analysisResults.findings.length > 0) {
                        console.log('Analysis data received:', response.data);
                        setAnalysisData(response.data);
                        setLoadingFindings(false);
                    } else {
                        // Continue polling only if still loading and mounted
                        if (loadingFindings && isMounted) {
                            setFindingsPollingAttempts(prev => prev + 1);
                            timeoutId = setTimeout(pollForFindings, 5000);
                        }
                    }
                });

            } catch (err) {
                console.error('Error polling for findings data:', err);
                if (loadingFindings && isMounted) {
                    setFindingsPollingAttempts(prev => prev + 1);
                    timeoutId = setTimeout(pollForFindings, 5000);
                }
            }
        };

        if (reportId && loadingFindings && !isRefusalReport) {
            pollForFindings();
        } else if (isRefusalReport) {
            // Skip polling for refusal reports
            setLoadingFindings(false);
        }

        // Cleanup timeout on unmount or when loading stops
        return () => {
            isMounted = false;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [reportId, loadingFindings, isRefusalReport]); // Removed findingsPollingAttempts from dependencies to prevent spam

    const getSeverityColor = (severity) => {
        switch (severity?.toLowerCase()) {
            case 'critical':
                return '#dc3545';
            case 'moderate':
                return '#ffc107';
            case 'minor':
                return '#17a2b8';
            default:
                return '#6c757d';
        }
    };

    const getSeverityIcon = (severity) => {
        switch (severity?.toLowerCase()) {
            case 'critical':
                return '🚨';
            case 'moderate':
                return '⚠️';
            case 'minor':
                return 'ℹ️';
            default:
                return '❓';
        }
    };

    const getComplianceColor = (compliance) => {
        switch (compliance?.toLowerCase()) {
            case 'compliant':
                return '#28a745';
            case 'non-compliant':
                return '#dc3545';
            case 'partially compliant':
                return '#ffc107';
            default:
                return '#6c757d';
        }
    };


    if (error) {
        return (
            <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
                <h2 className='module-title'>Clinical Feedback</h2>
                <div style={{ 
                    textAlign: 'center', 
                    padding: '20px', 
                    color: '#dc3545',
                    backgroundColor: '#f8d7da',
                    borderRadius: '8px',
                    border: '1px solid #f5c6cb'
                }}>
                    <div>{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
            <h2 className='module-title'>Clinical Feedback</h2>
            
            {isRefusalReport ? (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '30px',
                    backgroundColor: '#f8d7da',
                    borderRadius: '8px',
                    border: '2px solid #dc3545',
                    margin: '20px 0'
                }}>
                    <div style={{ fontSize: '24px', marginBottom: '15px', color: '#dc3545', fontWeight: 'bold' }}>
                        🚫 Clinical Analysis Not Available
                    </div>
                    <div style={{ fontSize: '18px', color: '#721c24', marginBottom: '15px' }}>
                        Clinical protocol analysis is not performed on patient refusal reports
                    </div>
                    <div style={{ 
                        padding: '20px',
                        backgroundColor: '#f5c6cb',
                        borderRadius: '8px',
                        border: '1px solid #f1aeb5'
                    }}>
                        <p style={{ margin: 0, color: '#721c24', fontSize: '16px', lineHeight: '1.5' }}>
                            Select a non-refusal report to view clinical feedback.
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Document Retrieval Flow */}
                    <div style={{ marginBottom: '30px' }}>
                        <h3 style={{ marginBottom: '15px', color: '#495057' }}>Document Retrieval Flow</h3>
                        <p style={{ marginBottom: '15px', color: '#6c757d', fontSize: '14px' }}>
                            This diagram shows how CADMUS retrieves and organizes documentation for clinical protocol analysis.
                        </p>
                        {loadingFlow ? (
                            <div style={{ 
                                textAlign: 'center', 
                                padding: '40px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                margin: '20px 0'
                            }}>
                                <div style={{ color: '#6c757d', marginBottom: '20px' }}>
                                    Retrieving document flow data
                                </div>
                                <div style={{ 
                                    display: 'inline-block',
                                    width: '40px',
                                    height: '40px',
                                    border: '4px solid #f3f3f3',
                                    borderTop: '4px solid var(--blue)',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                }}></div>
                            </div>
                        ) : flowData ? (
                            FlowComponent
                        ) : (
                            <div style={{ 
                                textAlign: 'center', 
                                padding: '20px', 
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                color: '#6c757d'
                            }}>
                                Document retrieval flow data not available
                            </div>
                        )}
                    </div>

                    {/* Analysis Results */}
                    {analysisData && analysisData.analysisResults ? (
                <div>
                    <h3 style={{ marginBottom: '15px', color: '#495057' }}>Clinical Analysis Results</h3>
                    
                    {/* Analysis Overview */}
                    <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <h4 style={{ margin: 0, color: '#495057' }}>Analysis Overview</h4>
                            <span style={{ fontSize: '0.9em', color: '#6c757d' }}>
                                {new Date(analysisData.timestamp).toLocaleString()}
                            </span>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ 
                                    fontSize: '24px', 
                                    fontWeight: 'bold',
                                    color: getComplianceColor(analysisData.analysisResults.overall_compliance)
                                }}>
                                    {analysisData.analysisResults.overall_compliance?.toUpperCase() || 'UNKNOWN'}
                                </div>
                                <div style={{ fontSize: '0.9em', color: '#6c757d' }}>Overall Compliance</div>
                            </div>
                            
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#495057' }}>
                                    {analysisData.analysisResults.statistics?.total_findings || 0}
                                </div>
                                <div style={{ fontSize: '0.9em', color: '#6c757d' }}>Total Findings</div>
                            </div>
                            
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>
                                    {analysisData.analysisResults.statistics?.critical_findings || 0}
                                </div>
                                <div style={{ fontSize: '0.9em', color: '#6c757d' }}>Critical</div>
                            </div>
                            
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffc107' }}>
                                    {analysisData.analysisResults.statistics?.moderate_findings || 0}
                                </div>
                                <div style={{ fontSize: '0.9em', color: '#6c757d' }}>Moderate</div>
                            </div>
                            
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#17a2b8' }}>
                                    {analysisData.analysisResults.statistics?.minor_findings || 0}
                                </div>
                                <div style={{ fontSize: '0.9em', color: '#6c757d' }}>Minor</div>
                            </div>
                        </div>
                    </div>

                    {/* Summary */}
                    {analysisData.analysisResults.summary && (
                        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e9ecef', borderRadius: '8px' }}>
                            <h4 style={{ margin: '0 0 10px 0', color: '#495057' }}>Summary</h4>
                            <p style={{ margin: 0, lineHeight: '1.5', color: '#495057' }}>
                                {analysisData.analysisResults.summary}
                            </p>
                        </div>
                    )}

                    {/* Findings */}
                    {analysisData.analysisResults.findings && analysisData.analysisResults.findings.length > 0 ? (
                        <div>
                            <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>Detailed Findings</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                {analysisData.analysisResults.findings.map((finding, index) => (
                                    <div 
                                        key={finding.id || index}
                                        style={{ 
                                            border: `2px solid ${getSeverityColor(finding.severity)}`,
                                            borderRadius: '8px',
                                            padding: '15px',
                                            backgroundColor: '#fff'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                                            <span style={{ fontSize: '20px', marginRight: '10px' }}>
                                                {getSeverityIcon(finding.severity)}
                                            </span>
                                            <h5 style={{ 
                                                margin: 0, 
                                                color: getSeverityColor(finding.severity),
                                                textTransform: 'uppercase'
                                            }}>
                                                {finding.severity} - {finding.type}
                                            </h5>
                                        </div>
                                        
                                        <div style={{ marginBottom: '10px' }}>
                                            <strong style={{ color: '#495057' }}>Issue:</strong>
                                            <p style={{ margin: '5px 0 0 0', lineHeight: '1.4' }}>
                                                {finding.issue}
                                            </p>
                                        </div>
                                        
                                        <div style={{ marginBottom: '10px' }}>
                                            <strong style={{ color: '#495057' }}>Protocol:</strong>
                                            <p style={{ margin: '5px 0 0 0', lineHeight: '1.4' }}>
                                                {finding.protocol}
                                            </p>
                                        </div>
                                        
                                        <div>
                                            <strong style={{ color: '#495057' }}>Evidence:</strong>
                                            <p style={{ margin: '5px 0 0 0', lineHeight: '1.4' }}>
                                                {finding.evidence}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '20px', 
                            backgroundColor: '#d4edda', 
                            borderRadius: '8px',
                            border: '1px solid #c3e6cb'
                        }}>
                            <div style={{ fontSize: '18px', color: '#155724', marginBottom: '5px' }}>
                                ✅ No Clinical Findings Identified
                            </div>
                            <div style={{ color: '#155724' }}>
                                The report appears to be compliant with all applicable clinical protocols.
                            </div>
                        </div>
                    )}
                </div>
            ) : loadingFindings ? (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '40px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    margin: '20px 0'
                }}>
                    {/* <div style={{ fontSize: '18px', marginBottom: '10px', color: '#495057' }}>
                        🔍 Processing Clinical Analysis...
                    </div> */}
                    <div style={{ color: '#6c757d', marginBottom: '20px' }}>
                        Clinical feedback is still processing and won't take more than 40 seconds
                    </div>
                    <div style={{ 
                        display: 'inline-block',
                        width: '40px',
                        height: '40px',
                        border: '4px solid #f3f3f3',
                        borderTop: '4px solid var(--blue)',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    {/* <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                        Attempt {findingsPollingAttempts + 1} of {maxAttempts}
                    </div> */}
                </div>
            ) : (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '20px', 
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    color: '#6c757d'
                }}>
                    Clinical analysis results not available
                </div>
            )}
                </>
            )}
        </div>
    );
};

export default DemoClinicalFeedbackModule;
